import type {ReactNode} from 'react';

import {useAppLocation, useAppNavigate} from './navigation/index.js';
import {reportError} from '../lib/clientErrorReporting.js';
import Header from '../shared/Header.tsx';
import {PageErrorBoundary} from '../shared/PageStates.js';

// ============================================================================
// PageBoundary — a page's crash stays in that page (Design Constitution N12)
// ----------------------------------------------------------------------------
// Wraps the routed page inside each entry (and App's released surfaces), so a
// render error in one page shows the header and 'Something Went Wrong' (green
// Reload, secondary Go Home) while App itself — its view-to-URL effect, auth
// and the navigation shell — stays mounted: the header still navigates.
// Moving to another route clears it. The outer ErrorBoundary in main.tsx
// stays the last resort for anything this boundary cannot draw (a crash in
// the header itself, or in App).
// ============================================================================

export default function PageBoundary({children}: {children: ReactNode}) {
  const location = useAppLocation();
  const navigate = useAppNavigate();
  return (
    <PageErrorBoundary
      resetKey={location.pathname}
      onGoHome={() => navigate('/')}
      header={<Header />}
      onError={(error, info) =>
        reportError('PageBoundary', error, {
          componentStack: info && info.componentStack ? info.componentStack.split('\n').slice(0, 5).join('\n') : null,
        })
      }
    >
      {children}
    </PageErrorBoundary>
  );
}
